import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { MapPin, Loader2, RefreshCw, Search } from 'lucide-react';
import { useNearbyRestaurants, NearbyPlace } from '@/hooks/useNearbyRestaurants';
import { useLocation } from '@/contexts/LocationContext';

const AUTOCOMPLETE_TYPES = ['restaurant', 'cafe', 'bar', 'bakery', 'fast_food_restaurant'];

interface NearbyRestaurantSelectorProps {
  onSelect: (data: NearbyPlace) => void;
}

export const NearbyRestaurantSelector: React.FC<NearbyRestaurantSelectorProps> = ({ onSelect }) => {
  const { places, isLoading, error, refetch } = useNearbyRestaurants();
  const { activeLocation, getRadiusInKm } = useLocation();

  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [scriptReady, setScriptReady] = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Load Google Maps JS script (needed for AutocompleteSuggestion)
  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) return;

    const isMapsReady = () => !!(window as any).google?.maps?.importLibrary;
    if (isMapsReady()) { setScriptReady(true); return; }

    const pollId = setInterval(() => {
      if (isMapsReady()) { setScriptReady(true); clearInterval(pollId); }
    }, 100);

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }

    return () => clearInterval(pollId);
  }, []);

  const fetchSuggestions = useCallback(async (input: string) => {
    if (!scriptReady) return;
    try {
      const { AutocompleteSuggestion } = await (window as any).google.maps.importLibrary('places');
      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        includedPrimaryTypes: AUTOCOMPLETE_TYPES,
        locationBias: {
          center: { lat: activeLocation.latitude, lng: activeLocation.longitude },
          radius: getRadiusInKm() * 1000,
        },
      });
      setSuggestions(results ?? []);
    } catch (err) {
      console.error('Autocomplete error:', err);
      setSuggestions([]);
    } finally {
      setIsSuggesting(false);
    }
  }, [scriptReady, activeLocation.latitude, activeLocation.longitude, getRadiusInKm]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);

    if (val.length < 2) {
      setSuggestions([]);
      return;
    }

    setIsSuggesting(true);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const handleSuggestionSelect = async (suggestion: any) => {
    const mainText = suggestion.placePrediction.mainText.toString();
    const fullText = suggestion.placePrediction.text.toString();
    try {
      const place = suggestion.placePrediction.toPlace();
      await place.fetchFields({ fields: ['location', 'addressComponents', 'formattedAddress', 'displayName'] });
      const countryCode = place.addressComponents
        ?.find((c: any) => c.types?.includes('country'))?.shortText;
      onSelect({
        name: mainText,
        place_id: suggestion.placePrediction.placeId,
        address: place.formattedAddress || fullText,
        lat: place.location.lat(),
        lng: place.location.lng(),
        country_code: countryCode,
      });
    } catch (err) {
      console.error('Error fetching place details:', err);
    }
  };

  const isSearching = query.length >= 2;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-foreground">
          {isSearching ? 'Search Results' : 'Nearby Restaurants'}
        </label>
        {!isSearching && (
          <Button
            variant="ghost"
            size="sm"
            onClick={refetch}
            className="h-7 px-2 text-muted-foreground hover:text-foreground"
            disabled={isLoading}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      </div>

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={handleQueryChange}
          placeholder="Search for a restaurant..."
          className="pl-9 bg-background border-border"
        />
      </div>

      {/* Search mode: autocomplete results */}
      {isSearching ? (
        isSuggesting ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : suggestions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">No restaurants found.</p>
        ) : (
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {suggestions.map((s) => (
              <li key={s.placePrediction.placeId}>
                <button
                  onMouseDown={(e) => { e.preventDefault(); handleSuggestionSelect(s); }}
                  className="w-full flex items-start gap-3 p-3 bg-background border border-border rounded-lg hover:border-[#cfef00] hover:bg-[#cfef00]/5 transition-colors text-left"
                >
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {s.placePrediction.mainText.toString()}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {s.placePrediction.secondaryText?.toString()}
                    </p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )
      ) : (
        /* Nearby mode */
        isLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="text-center py-8 space-y-3">
            <p className="text-sm text-muted-foreground">{error}</p>
            <Button variant="outline" size="sm" onClick={refetch}>
              Try again
            </Button>
          </div>
        ) : places.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8">
            No restaurants found nearby.
          </p>
        ) : (
          <ul className="space-y-2 max-h-64 overflow-y-auto">
            {places.map((place) => (
              <li key={place.place_id}>
                <button
                  onClick={() => onSelect(place)}
                  className="w-full flex items-start gap-3 p-3 bg-background border border-border rounded-lg hover:border-[#cfef00] hover:bg-[#cfef00]/5 transition-colors text-left"
                >
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{place.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{place.address}</p>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};
